// Self-service account page: profile (name) + password change.

import { useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';

export function SettingsPage() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name ?? '');
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileInfo, setProfileInfo] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwInfo, setPwInfo] = useState<string | null>(null);
  const [savingPw, setSavingPw] = useState(false);

  async function onSaveProfile(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setProfileError(null); setProfileInfo(null); setSavingProfile(true);
    try {
      await api('/api/auth/me', { method: 'PUT', body: JSON.stringify({ name }) });
      await refresh();
      setProfileInfo('Saved.');
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Save failed');
    } finally { setSavingProfile(false); }
  }

  async function onChangePw(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPwError(null); setPwInfo(null);
    if (newPassword.length < 12) { setPwError('New password must be at least 12 characters.'); return; }
    setSavingPw(true);
    try {
      await api('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setPwInfo('Password updated.');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed';
      if (msg === 'invalid_current_password') setPwError('Your current password is incorrect.');
      else setPwError(msg);
    } finally { setSavingPw(false); }
  }

  return (
    <div className="page-content">
      <header className="dashboard-header">
        <h1>Settings</h1>
        <p>Manage your account.</p>
      </header>

      <h2 style={{ fontSize: 16, fontWeight: 600, margin: '16px 0 8px' }}>Profile</h2>
      {profileError && <div className="form-error">{profileError}</div>}
      {profileInfo && <div className="info-banner">{profileInfo}</div>}
      <form onSubmit={(e) => void onSaveProfile(e)} className="admin-form" style={{ maxWidth: 480 }}>
        <div className="form-group">
          <label>Email</label>
          <input type="email" value={user?.email ?? ''} disabled />
        </div>
        <div className="form-group">
          <label>Name</label>
          <input type="text" required value={name} onChange={(e) => setName(e.target.value)} disabled={savingProfile} />
        </div>
        <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="submit" className="btn-primary" disabled={savingProfile}>{savingProfile ? 'Saving…' : 'Save profile'}</button>
        </div>
      </form>

      <h2 style={{ fontSize: 16, fontWeight: 600, margin: '24px 0 8px' }}>Change password</h2>
      {pwError && <div className="form-error">{pwError}</div>}
      {pwInfo && <div className="info-banner">{pwInfo}</div>}
      <form onSubmit={(e) => void onChangePw(e)} className="admin-form" style={{ maxWidth: 480 }}>
        <div className="form-group">
          <label>Current password</label>
          <input type="password" required autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} disabled={savingPw} />
        </div>
        <div className="form-group">
          <label>New password</label>
          <input type="password" required minLength={12} autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={savingPw} />
        </div>
        <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="submit" className="btn-primary" disabled={savingPw}>{savingPw ? 'Saving…' : 'Update password'}</button>
        </div>
      </form>
    </div>
  );
}
